// @flow
import React from 'react';
import classNames from 'classnames';
import snakeToTitleCase from '../../utils/snakeToTitleCase';
import * as Icons from './Icons';

import type {IconName} from './Icons';

import './Icon.less';

/**
 * IconName is defined in Icons.js, but is aliased and exported here as a
 * convenience so consumers don't have to care
 */
type _IconName = IconName;
export type {_IconName as IconName};

/**
 * Union type of the valid `.svg-*` color classes defined by baseline, minus
 * the `svg` prefix
 * c.f. https://github.homeawaycorp.com/pages/ui-toolkit/baseline/release/docs/gt/atoms.html#svg-color-emphasis
 */
type BaselineSvgColor =
    | 'default'
    | 'brand'
    | 'success'
    | 'warning'
    | 'danger'
    | 'link-color'
    | 'gray'
    | 'lightgray'
    | 'white';

type Props = {
    color?: BaselineSvgColor,
    name: IconName,
    iconClass?: string,
    title?: string
};

/**
 * # SVG Icon Component
 *
 * To add a new SVG icon, duplicate an existing Icon<IconName>.js component
 * in this `./Icon/` directory and copy your optimized SVG into the designated
 * area.
 *
 * The name of the icon should be a visual description of the icon, not its
 * semantic meaning in the application. Describe what it looks like, not what
 * purpose it serves.
 *
 * Then, add it to the list of icons in `./Icons.js` (instructions included there).
 *
 * Once this is done, you can use that icon using this Icon component:
 *
 * ```js
 * // given an icon defined in `./Icon/IconMyIcon.js`
 * import Icon from './Icon';
 * render() {
 *     return <Icon name="my-icon" color="success" />;
 * }
 * ```
 *
 * ## Notes on icon svg conventions:
 *
 * Colors are applied via baseline css classes that supply _both_ a fill and stroke:
 *
 * ```css
 * .svg-sample-color {
 *     fill: #f00;
 *     stroke: #f00;
 * }
 * ```
 *
 * This means that the elements in your SVG need to be carefully audited to
 * ensure that the css class supplies the right colors in the right places,
 * as per the following guide:
 *
 * ```html
 * <svg class="svg-sample-color">
 *     <!-- will render as: fill=#f00 stroke=#f00 (will gain fill and stroke) -->
 *     <circle />
 *
 *     <!-- will render as: fill=#000 stroke=#f00 (will gain stroke, existing fill not overridden) -->
 *     <circle fill="#000" />
 *
 *     <!-- will render as: fill=#f00 stroke=#fff (will gain fill, existing stroke not overridden)-->
 *     <circle stroke="#fff" />
 *
 *     <!-- will render as: fill=none stroke=#f00 (will gain stroke, existing fill=none not overridden)-->
 *     <circle fill="none" />
 * </svg>
 * ```
 *
 * In many cases, you'll need to _delete_ the fill/stroke color supplied by the
 * exported SVG, and _add_ a fill/stroke="none" to make sure a path that should
 * only have a fill doesn't get a stroke when the css class is applied.
 *
 * This is super silly, and as soon as baseline supports text-color classes for
 * all the icon colors we need, we can switch to specifying colors via `currentColor`,
 * which inherits the current text color value.
 */
const Icon = ({name, color = 'default', ...restProps}: Props) => {
    const iconClass = restProps.iconClass || classNames('Icon', `svg-${color}`);
    const iconName = `Icon${snakeToTitleCase(name)}`;
    const IconComponent = Icons[iconName];
    return (
        <span className={iconClass} aria-hidden title={restProps.title}>
            <IconComponent {...restProps} />
        </span>
    );
};

export default Icon;
